import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';

const COMBOS_ONBOARDING = [
  { id: 'morning', name: 'Buenos días', emoji: '☀️', color: '#F59E0B', desc: 'Clima, tráfico y motivación' },
  { id: 'fitness', name: 'Deporte y Salud', emoji: '💪', color: '#10B981', desc: 'Ejercicio y nutrición' },
  { id: 'humor', name: 'Familia y Amigos', emoji: '👨‍👩‍👧', color: '#3B82F6', desc: 'Mensajes y planes' },
  { id: 'couple', name: 'Pareja y Amor', emoji: '❤️', color: '#EF4444', desc: 'Citas románticas' },
  { id: 'money', name: 'Trabajo y Estudio', emoji: '💼', color: '#8B5CF6', desc: 'Productividad' },
  { id: 'night', name: 'Estacional', emoji: '🎉', color: '#EAB308', desc: 'Eventos y fiestas' },
];

const INTROS = [
  {
    emoji: '🎧',
    title: 'Audios infinitos',
    text: 'Pulsa ▶️ y escucha audios encadenados. Filtra por temática y escucha solo lo que te gusta. Estar siempre informado nunca fue tan rápido y fácil.'
  },
  {
    emoji: '📲',
    title: 'De WhatsApp a AudioToo',
    text: '¿Te han enviado un audio? Compártelo desde WhatsApp y elige AudioToo para subirlo.'
  },
  {
    emoji: '↗',
    emojiColor: '#25D366',
    title: 'De AudioToo a WhatsApp',
    text: 'Usa el botón ECO en cualquier audio para reenviarlo a tus chats.'
  }
];

const OnboardingPage = ({ onComplete }) => {
  const [selected, setSelected] = useState([]);
  const [step, setStep] = useState(0); // 0-2: intros, 3: selección de combos
  const navigate = useNavigate();

  const toggleCombo = (id) => {
    setSelected(prev => 
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleContinue = () => {
    // Guardar preferencias en localStorage
    localStorage.setItem('audioyou_onboarding_done', 'true');
    localStorage.setItem('audioyou_favorite_combos', JSON.stringify(selected));
    
    if (onComplete) {
      onComplete(selected);
    } else {
      navigate('/');
    }
  };

  const handleSkip = () => {
    localStorage.setItem('audioyou_onboarding_done', 'true');
    if (onComplete) {
      onComplete([]);
    } else {
      navigate('/');
    }
  };

  // Pasos introductorios (0, 1, 2)
  if (step < 3) {
    const intro = INTROS[step];
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <span className="text-7xl mb-6" style={intro.emojiColor ? { color: intro.emojiColor } : undefined}>{intro.emoji}</span>
          <h1 className="text-2xl font-bold text-white mb-3">{intro.title}</h1>
          <p className="text-white/70 text-base max-w-sm">{intro.text}</p>
        </div>

        <div className="p-4 pb-8 space-y-3 max-w-lg mx-auto w-full">
          <button
            onClick={() => setStep(step + 1)}
            className="w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
            data-testid="onboarding-next"
          >
            {step === 2 ? 'Empezar' : 'Siguiente'}
            <ChevronRight size={20} />
          </button>

          <button
            onClick={handleSkip}
            className="w-full py-3 text-white/50 text-sm hover:text-white/70 transition-colors"
            data-testid="onboarding-skip"
          >
            Saltar por ahora
          </button>
        </div>
      </div>
    );
  }

  // Paso 3: selección de combos
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col">
      {/* Header */}
      <div className="pt-12 pb-6 px-6 text-center">
        <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-xl">
          <span className="text-4xl">🎙️</span>
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">¡Bienvenido a AudioToo!</h1>
        <p className="text-white/70 text-base">¿Qué quieres escuchar cada día?</p>
        <p className="text-white/50 text-sm mt-1">Elige uno o varios combos</p>
      </div>

      {/* Combos Grid */}
      <div className="flex-1 px-4 pb-4 overflow-auto">
        <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto">
          {COMBOS_ONBOARDING.map((combo) => {
            const isSelected = selected.includes(combo.id);
            return (
              <button
                key={combo.id}
                onClick={() => toggleCombo(combo.id)}
                className={`relative rounded-2xl p-4 text-left transition-all duration-200 border-2 ${
                  isSelected 
                    ? 'border-white scale-[1.02] shadow-lg' 
                    : 'border-transparent hover:scale-[1.01]'
                }`}
                style={{ backgroundColor: combo.color }}
                data-testid={`onboarding-combo-${combo.id}`}
              >
                {/* Check mark */}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center">
                    <Check size={16} className="text-green-600" />
                  </div>
                )}
                
                <span className="text-4xl block mb-2 drop-shadow-lg">{combo.emoji}</span>
                <p className="text-white font-bold text-lg drop-shadow">{combo.name}</p>
                <p className="text-white/80 text-xs mt-1">{combo.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="p-4 pb-8 space-y-3 max-w-lg mx-auto w-full">
        <button
          onClick={handleContinue}
          disabled={selected.length === 0}
          className={`w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${
            selected.length > 0
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
              : 'bg-white/10 text-white/40'
          }`}
          data-testid="onboarding-continue"
        >
          {selected.length > 0 ? (
            <>
              Empezar con {selected.length} combo{selected.length > 1 ? 's' : ''}
              <ChevronRight size={20} />
            </>
          ) : (
            'Selecciona al menos uno'
          )}
        </button>
        
        <button
          onClick={handleSkip}
          className="w-full py-3 text-white/50 text-sm hover:text-white/70 transition-colors"
          data-testid="onboarding-skip"
        >
          Saltar por ahora
        </button>
      </div>
    </div>
  );
};

export default OnboardingPage;
